import { Injectable, computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import type { PlatformConfig, PlatformId } from '../models/platform.model';

const STORAGE_KEY = 'loggic.platform';

/**
 * Gerencia qual plataforma (Trivolo/Taskeo/Premmio) está ativa.
 * Não há nada centralizado: a plataforma define contra qual API se autentica.
 *
 * O ambiente (prod/dev) é definido pelo build/deploy — `environment.ts` (prod) e
 * `environment.development.ts` (dev) trazem o `apiBaseUrl` já resolvido. Não há
 * troca de ambiente em runtime.
 */
@Injectable({ providedIn: 'root' })
export class PlatformService {
  readonly all: readonly PlatformConfig[] = environment.platforms;

  private readonly _selected = signal<PlatformConfig | null>(this.restore());
  readonly selected = this._selected.asReadonly();

  /** Build de desenvolvimento (definido pelo environment compilado, não em runtime). */
  readonly isDev = !environment.production;

  /** Base URL da API da plataforma ativa. */
  readonly apiBaseUrl = computed(() => this._selected()?.apiBaseUrl ?? '');

  readonly hasSelection = computed(() => this._selected() !== null);

  select(id: PlatformId): void {
    const platform = this.all.find((p) => p.id === id && p.enabled) ?? null;
    this._selected.set(platform);
    if (platform) {
      localStorage.setItem(STORAGE_KEY, platform.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  clear(): void {
    this._selected.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  private restore(): PlatformConfig | null {
    const id = localStorage.getItem(STORAGE_KEY) as PlatformId | null;
    if (!id) return null;
    return this.all.find((p) => p.id === id && p.enabled) ?? null;
  }
}
